import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import PageHead from "@/components/feature/PageHead";

const COMPANY_NAME = "NetBasis International";

const title = "Importmanagement";
const heroImage = "https://net-basis.net/Bilder/import-management-customs-clearance-dach-distribution.webp";
const heroAlt = "Gestapelte Frachtcontainer symbolisieren Importmanagement, Zollabwicklung und lokalen Vertrieb.";
const heroClaimLine1 = "Mehr als nur";
const heroClaimLine2 = "Transport!";
const benefitsTitle = "Wie Sie von unserer Expertise im Bereich __Importmanagement__ profitieren:";
const introText = "Effizientes Importmanagement für Ihren nahtlosen Marktzugang in der DACH-Region. Als Experten vor Ort agieren wir für ausländische ITK-Hersteller als voll integrierte lokale Importabteilung und starkes vertriebliches Bindeglied zu Ihren Zielmärkten.";
const p1Text = "NetBasis International dient seinen ausländischen Herstellerpartnern vor Ort als deren Importabteilung. Wir übernehmen alle wesentlichen Importaktivitäten sowie die lokale Vertriebs- und Marketingarbeit im deutschsprachigen Wirtschaftsraum, sodass unsere Partner unmittelbar von unserem lokalen Netzwerk und dem sofortigen Zugang zu den richtigen Geschäftspartnern profitieren.";
const p1Parts = p1Text.split(COMPANY_NAME);
const bottomClaim = "NetBasis International - drive to excellence with excellent drivers !";

const benefits = [
  "Komplette Übernahme des gesamten Importprozesses inklusive der professionellen Zollabwicklung",
  "Korrekte Klassifizierung von Produkten nach Zolltarifnummern und reibungsloser Informationsaustausch mit allen Handelspartnern",
  "Zuverlässige Sicherstellung korrekter länderspezifischer Produktkennzeichnungen und Verpackungen",
  "Rechtskonforme Durchführung aller Prozesse unter strikter Beachtung lokaler Verbote und Beschränkungen",
  "Umfassende Absicherung und Minimierung aller finanziellen und rechtlichen Risiken vor Ort",
  "Einwandfreie und termingerechte Logistik und Lieferung an lokale Händler sowie Distributoren",
  "Strategische Durchführung von zielgerichteter Werbung, Marketing und verkaufsfördernden Werbeaktionen",
  "Professionelle Planung, Organisation und Durchführung von sowie Teilnahme an relevanten Messen und Kongressen",
  "Zuverlässige Übernahme und Abwicklung des gesamten lokalen Rechnungs- und Finanzwesens",
];

export default function ImportmanagementDePage() {
  return (
    <div className="min-h-screen flex flex-col">
      <PageHead
        title="Importmanagement | NetBasis International"
        description="Reibungslose Abwicklung, logistische Koordination und Einhaltung aller lokalen Richtlinien für Ihre internationalen Warenströme."
        ogUrl="https://net-basis.net/de/services/business-development/importmanagement"
      />
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        {/* Hero image */}
        <section className="w-full bg-white py-4 md:py-6 lg:py-8">
          <div className="w-full max-w-[1100px] mx-auto">
            <div className="relative w-full h-[320px] md:h-[420px] lg:h-[500px] overflow-hidden">
              <img loading="lazy"
                src={heroImage}
                alt={heroAlt}
                title={title}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>

              <div className="absolute inset-0 z-20 flex items-start justify-start overflow-hidden pointer-events-none pt-10 sm:pt-16 md:pt-[58px] lg:pt-[72px] pl-8 sm:pl-8 md:pl-[62px] lg:pl-14">
                <p
                  className="text-3xl md:text-4xl lg:text-5xl font-normal text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)] text-left importmanagement-hero-text-slide im-hero-lh-15"
                  style={{ fontFamily: '"Open Sans", sans-serif' }}
                >
                  <span className="block md:inline">{heroClaimLine1}</span>
                  <span className="hidden md:inline">&nbsp;</span>
                  <span className="block md:inline">{heroClaimLine2}</span>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-6 md:py-8 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
              {title}
            </h2>

            <h1 className="font-semibold text-foreground-950 leading-relaxed text-sm md:text-base mb-4">
              {introText}
            </h1>

            <div className="space-y-4 text-foreground-950 leading-relaxed text-sm md:text-base">
              <p>
                {p1Parts.map((part, i) => (
                  <span key={i}>
                    {part}
                    {i < p1Parts.length - 1 && (
                      <strong className="text-primary-600">{COMPANY_NAME}</strong>
                    )}
                  </span>
                ))}
              </p>
            </div>

            {/* Green heading with italic word */}
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mt-8 md:mt-10 mb-5 md:mb-7">
              {benefitsTitle.split("__").map((part, i) =>
                i % 2 === 1 ? <em key={i}>{part}</em> : part
              )}
            </h2>

            {/* Benefits with green checkmarks */}
            <div className="space-y-1.5 md:space-y-2 text-foreground-950 leading-relaxed text-sm md:text-base">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-start gap-2 md:gap-3">
                  <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center mt-0.5">
                    <i className="ri-check-line text-primary-600 text-base md:text-lg"></i>
                  </div>
                  <p>{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom claim */}
        <section className="py-6 md:py-10 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto text-center">
            <p className="font-heading text-lg md:text-xl font-bold text-primary-600">
              {bottomClaim}
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}